// Copyright © 2026 Phaena Management B.V. All rights reserved.
// Proprietary and confidential. See COPYRIGHT.md in the repository root.

/**
 * Top-level text-analytics-landing shell.
 *
 * Same shape as the Sparrow / Quill shells — thin top nav + content
 * + footer — so the apps feel like products from one shop. Brand-
 * distinct via the ink-teal Wordmark + paper background from
 * index.css.
 *
 * The landing page (`/`) lets the hero own the full canvas; every
 * subpage gets the standard padded width.
 */

import { NavLink, Outlet, Link, useLocation } from "react-router-dom"
import { Wordmark } from "@/brand/Wordmark"
import { READER_URL } from "@/lib/links"
import { ArrowUpRight, Home, ShieldCheck, Sparkles } from "lucide-react"

const NAV = [
  { to: "/", label: "Overview", icon: Home },
  { to: "/privacy", label: "Privacy", icon: ShieldCheck },
] as const

export function AppShell() {
  const loc = useLocation()
  const isLanding = loc.pathname === "/"
  return (
    <div className="min-h-screen flex flex-col text-foreground">
      <header className="sticky top-0 z-30 backdrop-blur-md bg-background/70 border-b border-border">
        <div className="mx-auto max-w-7xl flex items-center justify-between gap-6 px-6 h-14">
          <Link to="/" className="text-primary hover:opacity-80 transition-opacity">
            <Wordmark size="md" />
          </Link>
          <nav className="flex items-center gap-1">
            {NAV.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                end={to === "/"}
                className={({ isActive }) =>
                  [
                    "px-3 py-1.5 rounded-md text-sm font-medium inline-flex items-center gap-1.5 transition-colors",
                    isActive
                      ? "bg-secondary text-secondary-foreground"
                      : "text-muted-foreground hover:text-foreground hover:bg-secondary/60",
                  ].join(" ")
                }
              >
                <Icon className="h-4 w-4" />
                {label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <a
              href={READER_URL}
              className="hidden sm:inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-md bg-primary text-primary-foreground hover:opacity-90 transition-opacity"
            >
              <Sparkles className="h-3.5 w-3.5" />
              Open the Reader
              <ArrowUpRight className="h-3.5 w-3.5" />
            </a>
          </div>
        </div>
      </header>

      <main
        key={loc.pathname}
        className={
          (isLanding ? "flex-1" : "flex-1 mx-auto max-w-7xl w-full px-6 py-10") +
          " animate-page-fade"
        }
      >
        <Outlet />
      </main>

      <FooterMarker />
    </div>
  )
}

function FooterMarker() {
  return (
    <footer className="mt-auto border-t border-border">
      <div className="mx-auto max-w-7xl px-6 py-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-xs text-muted-foreground">
        <div className="flex items-center gap-2">
          <Wordmark size="sm" iconOnly className="text-primary" />
          <span>Wren — survey text, read locally.</span>
        </div>
        <div className="flex flex-wrap gap-4">
          <a href={READER_URL} className="hover:text-foreground transition-colors">
            wren.dasc.nl
          </a>
          <Link to="/privacy" className="hover:text-foreground transition-colors">
            Privacy
          </Link>
          <span>v0.1 marketing site</span>
        </div>
      </div>
    </footer>
  )
}
